import React, { useState, useEffect, useCallback, useRef } from 'react';

// Components
import ArticleList from './ArticleList';
import PullToRefreshIndicator from './PullToRefreshIndicator';
import ScrollToTopButton from './ScrollToTopButton';

// Hooks
import { usePullToRefresh } from '../hooks/usePullToRefresh';

// Utils
import { fetchArticles } from '../utils/api';
import { 
  getReadArticles, 
  generateSlug,
  trackEvent,
  trackError
} from '../utils/helpers';

// HomePage component
const HomePage = ({ selectedRegions, excludedCategories, onFilterClick, onArticleClick, onRefreshReady }) => {
  const [articles, setArticles] = useState([]);
  const [readArticles, setReadArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const offsetRef = useRef(0); // Use ref instead of state to prevent re-renders
  const [hasMore, setHasMore] = useState(true);
  const [newArticleIds, setNewArticleIds] = useState([]); // Track new articles after refresh
  const lastArticleIdBeforeRefreshRef = useRef(null); // Track last article ID before manual refresh

  // Load articles
  const loadArticles = useCallback(async (isRefresh = false) => {
    try {
      setError(null);
      const currentOffset = isRefresh ? 0 : offsetRef.current;
      const data = await fetchArticles(currentOffset, 20, selectedRegions, excludedCategories);
      // Backend returns array directly, not an object with articles property
      const articles = Array.isArray(data) ? data : (data.articles || []);
      
      if (isRefresh) {
        setArticles(articles);
        offsetRef.current = articles.length;
        
        // Identify new articles if we have a saved reference point
        if (lastArticleIdBeforeRefreshRef.current) {
          const lastArticleId = lastArticleIdBeforeRefreshRef.current;
          const newIds = [];
          
          // Find all articles that are newer than the last saved article ID
          for (const article of articles) {
            if (article.id === lastArticleId) {
              // Found the reference point, stop here
              break;
            }
            newIds.push(article.id);
          }
          
          if (newIds.length > 0) {
            setNewArticleIds(newIds);
            trackEvent('new-articles-highlighted', { count: newIds.length });
          } else {
            setNewArticleIds([]);
          }
          
          // Reset the reference
          lastArticleIdBeforeRefreshRef.current = null;
        }
        
        trackEvent('articles-loaded-via-refresh', {
          count: articles.length,
          hasRegionFilter: selectedRegions.length > 0,
          hasCategoryFilter: excludedCategories.length > 0
        });
      } else {
        // Prevent duplicates when loading more articles
        setArticles(prev => {
          const existingIds = new Set(prev.map(a => a.id));
          const newArticles = articles.filter(article => !existingIds.has(article.id));
          return [...prev, ...newArticles];
        });
        offsetRef.current += articles.length;
        trackEvent('articles-loaded-via-scroll', {
          count: articles.length,
          offset: currentOffset
        });
      }
      
      // Check if we have more articles (if we got full limit, there might be more)
      setHasMore(articles.length === 20);
    } catch (err) {
      console.error('Error loading articles:', err);
      trackError('article-load-error', err, {
        isRefresh,
        offset: isRefresh ? 0 : offsetRef.current,
        regionCount: selectedRegions.length,
        excludedCategoryCount: excludedCategories.length
      });
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedRegions, excludedCategories]); // Removed offset to prevent dependency loop

  // Handle refresh
  const handleRefresh = useCallback(async (isManualRefresh = false) => {
    trackEvent(isManualRefresh ? 'manual-refresh-triggered' : 'pull-to-refresh-triggered');
    
    // Save the first article's ID before refreshing (if we have articles)
    if (articles.length > 0 && isManualRefresh) {
      lastArticleIdBeforeRefreshRef.current = articles[0].id;
      trackEvent('saved-last-article-before-refresh', { articleId: articles[0].id });
    }
    
    setIsLoading(true);
    await loadArticles(true);
    return Promise.resolve();
  }, [loadArticles, articles]);

  // Pull to refresh hook
  const { pullDistance, isRefreshing, isPulling, triggerRefresh } = usePullToRefresh(handleRefresh);

  // Expose refresh function to parent component
  useEffect(() => {
    if (onRefreshReady) {
      // Use triggerRefresh which shows the loading indicator
      onRefreshReady(triggerRefresh);
    }
  }, [triggerRefresh, onRefreshReady]);

  // Load more articles when scrolling to bottom
  useEffect(() => {
    const handleScroll = () => {
      if (isLoading || !hasMore) return;
      
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      // Trigger loading when user is 800px from bottom (roughly 2-3 articles early)
      if (scrollTop + clientHeight >= scrollHeight - 800) {
        trackEvent('scroll-triggered-loading', {
          offset: offsetRef.current,
          hasMore
        });
        setIsLoading(true);
        loadArticles(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLoading, hasMore, loadArticles]);

  // Initial load and reload when regions/categories change  
  useEffect(() => {
    let mounted = true;
    let isLoading = false;
    
    const initializeData = async () => {
      // Prevent duplicate calls
      if (isLoading || !mounted) return;
      isLoading = true;
      
      try {
        // Reset offset when filters change
        offsetRef.current = 0;
        
        // Load fresh articles with new filters
        setIsLoading(true);
        setError(null);
        
        const data = await fetchArticles(0, 20, selectedRegions, excludedCategories);
        const articles = Array.isArray(data) ? data : (data.articles || []);
        
        if (mounted) {
          setArticles(articles);
          offsetRef.current = articles.length;
          setHasMore(articles.length === 20);
          
          // Track with debug info
          trackEvent('articles-loaded-via-refresh', {
            count: articles.length,
            hasRegionFilter: selectedRegions.length > 0,
            hasCategoryFilter: excludedCategories.length > 0,
            regionCount: selectedRegions.length,
            excludedCategoryCount: excludedCategories.length
          });
          
          // Always load read articles
          const stored = getReadArticles();
          setReadArticles(stored);
        }
      } catch (err) {
        if (mounted) {
          console.error('Error loading articles:', err);
          trackError('article-load-error', err, {
            isRefresh: true,
            offset: 0,
            regionCount: selectedRegions.length,
            excludedCategoryCount: excludedCategories.length
          });
          setError(err.message);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };
    
    initializeData();
    
    return () => {
      mounted = false;
    };
  }, [selectedRegions, excludedCategories]);

  // Update read articles when returning to page
  useEffect(() => {
    const updateReadArticles = () => {
      const stored = getReadArticles();
      setReadArticles(stored);
    };

    // Update read articles when page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updateReadArticles();
      }
    };

    // Update read articles when window gains focus
    const handleFocus = () => {
      updateReadArticles();
    };

    // Listen for browser back/forward navigation
    const handlePopState = () => {
      updateReadArticles();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('popstate', handlePopState);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Handle article click
  const handleArticleClick = (article) => {
    trackEvent('article-opened', {
      id: article.id,
      source: article.source,
      category: article.category,
      hasImage: !!article.image_url
    });
    
    // Generate slug from title
    const slug = generateSlug(article.swedish_title || article.original_title);
    
    // Add history entry for back button support
    window.history.pushState(
      { 
        articleId: article.id, 
        slug: slug,
        article: article 
      }, 
      '', 
      `/artikel/${article.id}/${slug}`
    );
    
    // Mark article as read immediately in state
    if (!readArticles.includes(article.id)) {
      setReadArticles(prev => [...prev, article.id]);
    }
    
    // Remove from new articles list when clicked/read
    if (newArticleIds.includes(article.id)) {
      setNewArticleIds(prev => prev.filter(id => id !== article.id));
    }
    
    // Call parent's openModal function
    onArticleClick(article);
  };

  // Handle retry
  const handleRetry = () => {
    setIsLoading(true);
    loadArticles(true);
  };

  return (
    <>
      <PullToRefreshIndicator 
        pullDistance={pullDistance}
        isRefreshing={isRefreshing}
        isPulling={isPulling}
      />
      <ArticleList
        articles={articles}
        readArticles={readArticles}
        newArticleIds={newArticleIds}
        isLoading={isLoading}
        error={error}
        onArticleClick={handleArticleClick}
        onRetry={handleRetry}
        selectedRegions={selectedRegions}
        excludedCategories={excludedCategories}
      />
      <ScrollToTopButton />
    </>
  );
};

export default HomePage;
